import { BomNode } from "@/features/job-tracking/queries/tracking-data/bom-hierarchy-generator";
import { BomPathPurchasingPriority } from "@/features/job-tracking/queries/tracking-data/purchasing-priorities";

export type PriorityOption = (typeof priorityOptions)[number];

export type PrioritySourceType = "job" | "template" | "default";

export interface EffectivePriority {
  /** The priority number assigned to this node. Equal to `appliedRecord.priority` */
  priority: PriorityOption;

  /** The template-based purchasing priority database record found matching this node, if any. */
  nodeTemplateRecord: BomPathPurchasingPriority | undefined | null;

  /** The job-specific purchasing priority database record found matching this node, if any. */
  nodeJobRecord: BomPathPurchasingPriority | undefined | null;

  /**
   * The purchasing priority database record that was applied to this node. This may be a match for this node, or
   * inherited from a parent node. If a match for this node, the job-specific record will be preferred over the
   * template record.
   *
   * If this is a default priority, this will be `null` and the `appliedRecordSource` will be "default".
   */
  appliedRecord: BomPathPurchasingPriority | undefined | null;

  /** The source of the applied record, either 'job', 'template' or 'default' */
  appliedRecordSource: PrioritySourceType;

  /** Whether the applied record was inherited or not. */
  inherited: boolean;

  /** A collection of child nodes (recursive) that do not inherit the purchasing priority from their parent */
  childOverrides: {
    /** Nodes that have a purchasing priority set for the current job */
    job: BomNode[];
    /** Nodes that have a purchasing priority set from a template */
    template: BomNode[];
  };
}

export const priorityOptions = [1, 2, 3, 4] as const;

export const minPriorityOption = 1 as const;

export const maxPriorityOption = 4 as const;

export const priorityOptionColors: Record<PriorityOption, string> = {
  1: "#A3A380", // sage/olive gray
  2: "#E2C290", // soft sand/tan
  3: "#B4B4B4", // light neutral gray
  4: "#A18FC6", // muted lavender purple
};

export const defaultPurchasingPriority: Omit<EffectivePriority, "childOverrides"> = {
  priority: 1,
  nodeTemplateRecord: null,
  nodeJobRecord: null,
  appliedRecord: null,
  appliedRecordSource: "default",
  inherited: false,
};

export function determineEffectivePurchasingPriority(
  parentEffectivePriority: Omit<EffectivePriority, "childOverrides">,
  nodeTemplateRecord: BomPathPurchasingPriority | undefined | null,
  nodeJobRecord: BomPathPurchasingPriority | undefined | null,
): Omit<EffectivePriority, "childOverrides"> {
  // The applied record always prefers the job-specific record over the template record
  const appliedRecord = nodeJobRecord ?? nodeTemplateRecord;

  // If this node does not have it's own explicit priority, inherit from the parent node
  if (!appliedRecord) return { ...parentEffectivePriority, nodeTemplateRecord, nodeJobRecord, inherited: true };

  // If this node has a template priority, but the parent priority is job-specific, inherit it (job-specific priorities always override template priorities)
  const sourceType = appliedRecord.jobId ? "job" : "template";
  if (parentEffectivePriority.appliedRecordSource === "job" && sourceType === "template")
    return { ...parentEffectivePriority, nodeTemplateRecord, nodeJobRecord, inherited: true };

  // Compute and return the overridden priority for the current node
  return {
    appliedRecord,
    nodeTemplateRecord,
    nodeJobRecord,
    appliedRecordSource: sourceType,
    priority: appliedRecord.priority,
    inherited: false,
  };
}

/** Get a list of all child nodes (recursively) that do not inherit the purchasing priority from their parent */
export function getChildPurchasingPriorityOverrides(children: BomNode[]): EffectivePriority["childOverrides"] {
  return children.reduce<EffectivePriority["childOverrides"]>(
    (overrides, child) => {
      // Include this child if it is an override
      if (!child.purchasingPriority.inherited && child.purchasingPriority.appliedRecordSource !== "default")
        overrides[child.purchasingPriority.appliedRecordSource].push(child);

      // Include all the overridden children of this child
      overrides.job.push(...child.purchasingPriority.childOverrides.job);
      overrides.template.push(...child.purchasingPriority.childOverrides.template);

      return overrides;
    },
    { job: [], template: [] },
  );
}
